import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";

interface IInspectionData {
	id: string;
	name: string;
	lastDone: string;
	location: string;
	dueDate: string;
	dueInDays?: number;
}

const inspectionData: IInspectionData[] = [
	{
		id: "1",
		name: "Sire",
		lastDone: "2025-08-10",
		location: "Castellon, Spain",
		dueDate: "2025-08-12",
	},
	{
		id: "2",
		name: "PSC",
		lastDone: "2025-03-18",
		location: "Pascagoula, US",
		dueDate: "2025-04-24",
	},
	{
		id: "3",
		name: "Marine Inspection",
		lastDone: "2025-02-21",
		location: "Castellon, Spain",
		dueDate: "2025-02-27",
	},
	{
		id: "4",
		name: "USCG COC",
		lastDone: "2025-02-12",
		location: "Castellon, Spain",
		dueDate: "2025-02-18",
	},
	{
		id: "5",
		name: "MLC Intermediate Inspection",
		lastDone: "2025-01-25",
		location: "Castellon, Spain",
		dueDate: "2025-01-31",
	},
	{
		id: "6",
		name: "Internal Audit",
		lastDone: "2025-01-25",
		location: "Castellon, Spain",
		dueDate: "2025-01-31",
	},
];

// To calculate the difference in days between two dates
const getDaysUntilDue = (dueDate: string): number | null => {
	const now = new Date();
	const due = new Date(dueDate);
	// Use UTC to avoid timezone-related off-by-one errors
	const utcNow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
	const utcDue = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate());
	const diffTime = utcDue - utcNow;
	if (diffTime < 0) return null; // Return null if the date is in the past
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	return diffDays;
};

// To format an ISO date string into "DD Mon YYYY" format
const formatDateAsDDMonYYYY = (isoDate: string): string => {
	const date = new Date(isoDate);

	return date.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});
};

const DueDateBadge: React.FC<{ days: number | null | undefined }> = ({
	days,
}) => {
	// Only show for dates due within the next 7 days
	if (days === null || days === undefined || days > 7) {
		return null;
	}

	let text = "";
	if (days === 0) {
		text = "Due today";
	} else if (days === 1) {
		text = "Due in a day";
	} else {
		text = `Due in ${days} days`;
	}

	return (
		<span className="ml-4 inline-flex items-center bg-red-100 border border-red-300 text-red-600 text-xs font-medium px-2.5 py-[3px] rounded-full">
			<span className="w-1.5 h-1.5 mr-1.5 bg-red-500 rounded-full"></span>
			{text}
		</span>
	);
};

type OwnProps = {
	vesselId: string;
};
type VDMDashboardInspectionProps = PropsFromRedux & OwnProps;
function VDMDashboardInspection(props: VDMDashboardInspectionProps) {
	const processedData = inspectionData.map((item) => {
		const dueInDays = getDaysUntilDue(item.dueDate);
		return {
			...item,
			lastDone: formatDateAsDDMonYYYY(item.lastDone),
			dueDate: item.dueDate,
			dueInDays,
		};
	});

	return (
		<div className="bg-white rounded-lg shadow-sm overflow-x-auto mt-6 border border-gray-200">
			<table className="w-full border-collapse text-[12px] xl:text-sm">
				<thead>
					<tr className="text-left text-gray-500 font-medium">
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">
							Name
						</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">
							Last Done
						</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">
							Location
						</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">
							Due Date
						</th>
						<th className="p-2 font-semibold whitespace-nowrap text-xs xl:text-[12px]">
							Actions
						</th>
					</tr>
				</thead>
				<tbody>
					{processedData?.map((inspection) => {
						const { name, dueDate, lastDone, id, location, dueInDays } =
							inspection;
						return (
							<React.Fragment key={id}>
								<tr
									className={`border-t border-gray-200 hover:bg-gray-50 cursor-pointer `}
								>
									<td className="p-2  text-gray-700 whitespace-nowrap">
										{name}
									</td>
									<td className="p-2 text-gray-700 whitespace-nowrap">
										{lastDone}
									</td>
									<td className="p-2 text-gray-700 whitespace-nowrap">
										{location}
									</td>
									<td className="p-2 whitespace-nowrap text-sm text-gray-700">
										<span>{dueDate}</span>
										<DueDateBadge days={dueInDays} />
									</td>
									<td className="p-2 whitespace-nowrap text-sm font-medium">
										<div className="text-blue-600 hover:text-blue-800">
											View Report
										</div>
									</td>
								</tr>
							</React.Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(VDMDashboardInspection);
